import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    X, BarChart3, LineChart as LineChartIcon, Table2,
    CalendarRange, Loader2, Info, ChevronDown, ChevronUp, Search, User, Calendar,
    ArrowUp, ArrowDown, Minus, Download
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import {
    ResponsiveContainer, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import FunilDinamico from './FunilDinamico';
import { buildSheet, downloadWorkbook, fileStamp } from '../../../src/lib/excelExport';
import { ehVendaEfetiva } from '../../../src/lib/dashboard/vendasEfetivas';

interface KanbanGraphicProps {
    isOpen: boolean;
    onClose: () => void;
    activePipelineId: string | null;
    selectedResponsibles: string[];
    onOpenNegociacao?: (id: number) => void;
}

interface NegociacaoRow {
    id: number;
    titulo?: string | null;
    created_at: string;
    updated_at: string;
    status: string;
    responsavel_id: string | null;
    consumo_medio_kwh: number | null;
    data_assinatura_termo: string | null;
    stage_changed_at: string | null;
}

// Badge de comparação vs período anterior (↑/↓ com %). `inverso` = aumentar é ruim.
const DeltaBadge: React.FC<{ atual: number; anterior: number; inverso?: boolean; suffix?: string }> = ({ atual, anterior, inverso = false, suffix = '' }) => {
    const diff = atual - anterior;
    const pct = anterior > 0 ? (diff / anterior) * 100 : (atual > 0 ? 100 : 0);
    const isUp = diff > 0;
    const isFlat = diff === 0;
    const good = isFlat ? null : (inverso ? !isUp : isUp);
    const color = good === null ? 'text-gray-400 dark:text-gray-500' : good ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400';
    const Icon = isFlat ? Minus : isUp ? ArrowUp : ArrowDown;
    return (
        <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${color}`} title="vs período anterior de mesma duração">
            <Icon size={10} />
            {isFlat ? '0' : `${Math.abs(diff)}${suffix}`}
            {anterior > 0 && !isFlat && <span className="opacity-70">({Math.abs(pct).toFixed(0)}%)</span>}
        </span>
    );
};

interface DayData {
    date: string;
    label: string;
    fullLabel: string;
    criadas: number;
    vendidas: number;
    perdidas: number;
}

interface ProfileItem {
    id: string;
    full_name: string;
    parceiro_id?: string;
    role?: string;
}

const KanbanGraphic: React.FC<KanbanGraphicProps> = ({
    isOpen,
    onClose,
    activePipelineId,
    selectedResponsibles: externalResponsibles,
    onOpenNegociacao,
}) => {
    const { profile, user } = useAuth();

    // Date filter — default to current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const [dateStart, setDateStart] = useState(firstDay.toISOString().split('T')[0]);
    const [dateEnd, setDateEnd] = useState(lastDay.toISOString().split('T')[0]);
    const [chartType, setChartType] = useState<'line' | 'table'>('line');
    const [compareMetric, setCompareMetric] = useState<'criadas' | 'vendidas' | 'perdidas'>('vendidas');
    const [onlyBusinessDays, setOnlyBusinessDays] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<NegociacaoRow[]>([]);
    const [excluidas, setExcluidas] = useState(0);
    const [perdaDateMap, setPerdaDateMap] = useState<Record<number, string>>({});
    // Decisão de análise cadastral por negociação, só das que caem na janela
    // buscada — fonte de verdade pra Aprovados/Reprovados. Não usa negociacoes.status
    // porque ele pode ficar sem sincronizar com a decisão real (achado em
    // 10/09/2026: negociação 6879 tinha decisao_final='Aprovado' na análise
    // cadastral e status ainda 'termo_assinado'), e decisao_final_at não sofre a
    // contaminação de updated_at pela transferência em massa de 02/09/2026.
    const [decisaoMap, setDecisaoMap] = useState<Record<number, { decisao: string; data: string }>>({});

    // Responsible selector state (internal to dashboard)
    const [profiles, setProfiles] = useState<ProfileItem[]>([]);
    const [dashboardResponsibles, setDashboardResponsibles] = useState<string[]>([]);
    const [responsibleMode, setResponsibleMode] = useState<'all' | 'mine' | 'custom'>('all');
    const [pendingResponsibles, setPendingResponsibles] = useState<string[]>([]);
    const [pendingMode, setPendingMode] = useState<'all' | 'mine' | 'custom'>('all');
    const [showResponsibleDropdown, setShowResponsibleDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isVendedor = profile?.role === 'vendedor' || profile?.role === 'vendedor_parceiro';
    const isComparative = responsibleMode === 'custom' && dashboardResponsibles.length >= 2;
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];

    // Fetch profiles
    useEffect(() => {
        if (!isOpen) return;
        const fetchProfiles = async () => {
            const { data } = await supabase.from('profiles').select('id, full_name, parceiro_id, departamento, role').eq('status', 'active').neq('role', 'consumidor');
            if (data) setProfiles(data);
        };
        fetchProfiles();
    }, [isOpen]);

    // Apply external responsibles if any
    useEffect(() => {
        if (externalResponsibles.length > 0) {
            setDashboardResponsibles(externalResponsibles);
            setResponsibleMode('custom');
        }
    }, [externalResponsibles]);

    // Force vendedor to only see own data
    useEffect(() => {
        if (isVendedor && user?.id) {
            setResponsibleMode('mine');
            setDashboardResponsibles([user.id]);
        }
    }, [isVendedor, user?.id]);

    // Fetch data
    useEffect(() => {
        if (!isOpen) return;
        fetchData();
    }, [isOpen, dateStart, dateEnd, activePipelineId, dashboardResponsibles, responsibleMode]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Janela da consulta: do início do período ANTERIOR (mesma duração, pros
            // deltas ↑↓) até o fim do período atual. Cobre os dois usos de `rows`
            // (computed + prevKpis) numa fetch só.
            //
            // Achado em 10/09/2026: sem filtro de data nenhum, esta consulta buscava
            // TODO o histórico da pessoa selecionada — pra alguém com milhares de
            // negociações (ex.: carteira herdada de outro vendedor desligado), isso
            // arrisca estourar o teto padrão de linhas do PostgREST e voltar um
            // recorte arbitrário, bem menor que o real. Foi exatamente o que
            // aconteceu: "Negociações Criadas" mostrava 67 de um total real de 289
            // — um bug de infraestrutura da consulta, não de critério de data.
            //
            // ⚠️ created_at, data_assinatura_termo e (via decisaoMap) decisao_final_at
            // entram no filtro. Deliberadamente NÃO uso negociacoes.updated_at nem a
            // data do log de "marcou como perdido" pra decidir quem entra: updated_at
            // está contaminado pela transferência em massa de 02/09/2026 (grava
            // updated_at=now() em milhares de linhas de uma vez, sem relação com
            // "mudou de status agora"), e a data do log de perda mistura limpeza
            // administrativa em lote do backlog herdado (achado em 10/09/2026: 549
            // negociações marcadas perdidas em rajadas de 30-70/hora pela mesma
            // pessoa) com perda de venda de verdade — Perdidas fica com o critério
            // antigo (criação) até o dono do produto decidir o que uma "limpeza de
            // backlog" deveria significar nesse número.
            const curStartB = new Date(dateStart + 'T00:00:00');
            const curEndB = new Date(dateEnd + 'T23:59:59');
            const lenMsB = curEndB.getTime() - curStartB.getTime();
            const rangeStartIso = new Date(curStartB.getTime() - lenMsB - 1).toISOString();
            const rangeEndIso = curEndB.toISOString();

            // Decisões de análise cadastral dentro da janela — busca à parte (mesma
            // técnica do perdaDateMap logo abaixo) porque a data mora numa tabela
            // ligada (analise_cadastral), não em negociacoes. Escopo global
            // (qualquer responsável): é só pra achar QUAIS ids entram na consulta
            // principal, que aplica o filtro de responsável de verdade depois.
            const { data: decisoesJanela } = await supabase
                .from('analise_cadastral')
                .select('negociacao_id, decisao_final, decisao_final_at')
                .in('decisao_final', ['Aprovado', 'Aprovado com Ressalvas', 'Reprovado'])
                .gte('decisao_final_at', rangeStartIso)
                .lte('decisao_final_at', rangeEndIso);

            const novoDecisaoMap: Record<number, { decisao: string; data: string }> = {};
            (decisoesJanela || []).forEach((d: any) => {
                // Mais de uma análise pra mesma negociação na janela: fica a mais recente.
                const atual = novoDecisaoMap[d.negociacao_id];
                if (!atual || d.decisao_final_at > atual.data) {
                    novoDecisaoMap[d.negociacao_id] = { decisao: d.decisao_final, data: d.decisao_final_at };
                }
            });
            setDecisaoMap(novoDecisaoMap);
            const idsDecididosNaJanela = Object.keys(novoDecisaoMap).map(Number);

            let query = supabase
                .from('negociacoes')
                .select('id, titulo, created_at, updated_at, status, responsavel_id, consumo_medio_kwh, data_assinatura_termo, stage_changed_at')
                // Traz uma negociação se ela foi CRIADA na janela (KPI "Criadas" e base
                // de Perdidas, que continua por data de criação) OU ASSINADA na janela
                // (não importa quando foi criada — um contrato criado em agosto e
                // assinado em setembro tem que aparecer no relatório de setembro) OU
                // teve uma decisão de análise cadastral na janela (mesmo raciocínio,
                // pra Aprovados/Reprovados).
                .or(
                    [
                        `and(created_at.gte.${rangeStartIso},created_at.lte.${rangeEndIso})`,
                        `and(data_assinatura_termo.gte.${rangeStartIso},data_assinatura_termo.lte.${rangeEndIso})`,
                        ...(idsDecididosNaJanela.length > 0 ? [`id.in.(${idsDecididosNaJanela.join(',')})`] : []),
                    ].join(',')
                )
                .order('created_at', { ascending: false })
                // Teto explícito, bem acima de qualquer par mês-atual/mês-anterior real
                // (a pessoa mais ativa do sistema hoje tem ~300/mês) — não depende de
                // adivinhar se o projeto tem (ou um dia vai ter) um limite padrão de
                // linhas por resposta.
                .limit(5000);

            // Apply responsibility filter
            if (responsibleMode === 'mine' && user?.id) {
                query = query.eq('responsavel_id', user.id);
            } else if (responsibleMode === 'custom' && dashboardResponsibles.length > 0) {
                query = query.in('responsavel_id', dashboardResponsibles);
            } else if (responsibleMode === 'all' && profile?.role === 'parceiro') {
                const { data: managed } = await supabase.from('profiles').select('id').eq('parceiro_id', profile.id);
                const managedIds = managed?.map((m: any) => m.id) || [];
                query = query.in('responsavel_id', [profile.id, ...managedIds]);
            } else if (responsibleMode === 'all' && profile?.role === 'gerente_parcerias') {
                const { data: parceiroUsers } = await supabase.from('profiles').select('id').in('role', ['parceiro', 'vendedor_parceiro']);
                const parceiroIds = parceiroUsers?.map((m: any) => m.id) || [];
                query = query.in('responsavel_id', [profile.id, ...parceiroIds]);
            }
            // 'all' → no filter (unless vendedor, handled above, and parceiro handled above)

            const { data, error } = await query;
            if (error) throw error;
            setRows(data || []);

            // Timestamps reais de perda via negociacao_logs (mais preciso que updated_at)
            const perdidoIds = (data || []).filter(r => r.status === 'perdido').map(r => r.id);
            if (perdidoIds.length > 0) {
                const { data: perdaLogs } = await supabase
                    .from('negociacao_logs')
                    .select('negociacao_id, created_at')
                    .eq('acao', 'marcou como perdido')
                    .in('negociacao_id', perdidoIds);
                const map: Record<number, string> = {};
                (perdaLogs || []).forEach((l: any) => {
                    if (!map[l.negociacao_id] || l.created_at > map[l.negociacao_id]) {
                        map[l.negociacao_id] = l.created_at;
                    }
                });
                setPerdaDateMap(map);
            } else {
                setPerdaDateMap({});
            }

            // Negociações excluídas: lê da tabela de auditoria de deleções
            let exclQuery = supabase
                .from('negociacoes_excluidas_log')
                .select('id', { count: 'exact', head: true })
                .gte('excluido_em', new Date(dateStart + 'T00:00:00').toISOString())
                .lte('excluido_em', new Date(dateEnd + 'T23:59:59').toISOString());
            if (responsibleMode === 'mine' && user?.id) {
                exclQuery = exclQuery.eq('responsavel_id', user.id);
            } else if (responsibleMode === 'custom' && dashboardResponsibles.length > 0) {
                exclQuery = exclQuery.in('responsavel_id', dashboardResponsibles);
            }
            const { count: exclCount } = await exclQuery;
            setExcluidas(exclCount || 0);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Compute KPIs and chart data
            const computed = useMemo(() => {
        const start = new Date(dateStart + 'T00:00:00');
        const end = new Date(dateEnd + 'T23:59:59');
        const isComp = responsibleMode === 'custom' && dashboardResponsibles.length >= 2;

        // Base: todos criados no período — critério B (cohorte do período)
        const criadasInRange = rows.filter(r => {
            const d = new Date(r.created_at);
            if (onlyBusinessDays && (d.getDay() === 0 || d.getDay() === 6)) return false;
            return d >= start && d <= end;
        });

        // Data de um evento cai dentro do período selecionado? Mesmo teste usado
        // em criadasInRange, só que reaproveitável pra qualquer coluna de data.
        const dentroDoPeriodo = (iso: string | null) => {
            if (!iso) return false;
            const d = new Date(iso);
            if (onlyBusinessDays && (d.getDay() === 0 || d.getDay() === 6)) return false;
            return d >= start && d <= end;
        };

        // Termos Assinados: ASSINADOS dentro do período — não "criados no período E
        // já assinados (a qualquer momento)". Achado em 10/09/2026: esse critério
        // antigo escondia contratos criados num mês e assinados no seguinte de
        // QUALQUER relatório por período — nunca apareciam nem no mês da criação
        // (não tinham assinatura ainda) nem no mês da assinatura (não foram criados
        // nele). "Quantos contratos a Júlia assinou esse mês" é uma pergunta sobre
        // quando ela assinou, não sobre quando a negociação nasceu.
        const kpiAssinados = rows.filter(r => dentroDoPeriodo(r.data_assinatura_termo));

        // Gráfico usa o mesmo conjunto do card agora — antes eram dois cálculos
        // que deveriam bater e não batiam (um dos sintomas originais do bug).
        const vendidasInRange = kpiAssinados;

        // Termos Aprovados / Reprovados: pela data REAL da decisão (decisao_final_at
        // da análise cadastral), não pela data de criação. decisao_final_at não
        // sofre a contaminação de updated_at (ver comentário em fetchData).
        //
        // MAS decisao_final_at é gravado no primeiro save (handleSaveProgress),
        // não só quando a análise é de fato concluída — o analista pode deixar um
        // rascunho de decisão e sair sem clicar "Finalizar Análise" (às vezes de
        // propósito, adiando via adiarAnaliseTermo por faltar alguma confirmação).
        // Achado em 10/09/2026 com a negociação 6879 (Marília): decisao_final
        // ficou 'Aprovado' em rascunho, mas o botão "Finalizar Análise" nem pode
        // ser clicado sem verificação telefônica confirmada (requiresPhoneVerification
        // em AnaliseCadastralWizard.tsx) — não é bug de sincronização, é decisão
        // ainda não finalizada. Por isso o filtro também exige o status real da
        // negociação ter transitado (só executeFinalizar muda negociacoes.status),
        // senão rascunhos como esse eram contados como aprovação/reprovação real.
        const aprovadosInRange = rows.filter(r => {
            const dec = decisaoMap[r.id];
            return !!dec && (dec.decisao === 'Aprovado' || dec.decisao === 'Aprovado com Ressalvas')
                && dentroDoPeriodo(dec.data) && r.status === 'termo_aprovado';
        });
        const reprovadosInRange = rows.filter(r => {
            const dec = decisaoMap[r.id];
            return !!dec && dec.decisao === 'Reprovado' && dentroDoPeriodo(dec.data) && r.status === 'termo_reprovado';
        });

        // Vendas Efetivas (#6421): a turma ASSINADA no período que já foi aprovada. Não é o
        // mesmo que "Termos Aprovados", que conta pela data da aprovação e por isso traz termo
        // assinado no mês anterior — era essa mistura que fazia a conta da gerente não fechar.
        const efetivasInRange = rows.filter(r => ehVendaEfetiva(r, dentroDoPeriodo));

        // Negociações Perdidas: CONTINUA pela data de CRIAÇÃO, de propósito — tentei
        // trocar pra data do evento e voltei atrás em 10/09/2026: nem updated_at
        // (contaminado pela transferência em massa) nem a data real do log de
        // "marcou como perdido" servem aqui — o log mistura limpeza administrativa
        // em lote do backlog herdado (549 negociações fechadas em rajada por uma só
        // pessoa em 9 dias) com perda de venda de verdade, e as duas coisas não
        // fazem sentido somadas num "vendas perdidas este mês". Fica pendente de
        // decisão do dono do produto: contar limpeza de backlog como "perdida do
        // mês" ou não.
        const perdidasInRange = criadasInRange.filter(r => r.status === 'perdido');

        const sumKwh = (arr: NegociacaoRow[]) =>
            arr.reduce((acc, r) => acc + (r.consumo_medio_kwh || 0), 0);

        const criadasKwh = sumKwh(criadasInRange);
        const assinadosKwh = sumKwh(kpiAssinados);
        const aprovadosKwh = sumKwh(aprovadosInRange);
        const reprovadosKwh = sumKwh(reprovadosInRange);
        const efetivasKwh = sumKwh(efetivasInRange);
        const perdidasKwh = sumKwh(perdidasInRange);

        // Tempo médio até assinatura: criados no período que foram assinados
        let avgDaysToWin = 0;
        if (kpiAssinados.length > 0) {
            const totalDays = kpiAssinados.reduce((acc, r) => {
                const diff = new Date(r.data_assinatura_termo!).getTime() - new Date(r.created_at).getTime();
                return acc + diff / (1000 * 60 * 60 * 24);
            }, 0);
            avgDaysToWin = Math.round(totalDays / kpiAssinados.length);
        }

        // Tempo médio até perda: usa timestamp real do log "marcou como perdido"
        let avgDaysToLose = 0;
        if (perdidasInRange.length > 0) {
            let total = 0, count = 0;
            perdidasInRange.forEach(r => {
                const lossDate = perdaDateMap[r.id] || r.updated_at;
                const diff = new Date(lossDate).getTime() - new Date(r.created_at).getTime();
                if (diff > 0) { total += diff / (1000 * 60 * 60 * 24); count++; }
            });
            avgDaysToLose = count > 0 ? Math.round(total / count) : 0;
        }

        const respStats: Record<string, { criadas: number, vendidas: number, aprovados: number, reprovados: number, efetivas: number, perdidas: number, criadasKwh: number, vendidasKwh: number, aprovadosKwh: number, reprovadosKwh: number, efetivasKwh: number, perdidasKwh: number, statusCounts: Record<string, number>, totalDaysToWin: number, totalDaysToLose: number }> = {};
        if (isComp) {
            dashboardResponsibles.forEach(id => {
                respStats[id] = { criadas: 0, vendidas: 0, aprovados: 0, reprovados: 0, efetivas: 0, perdidas: 0, criadasKwh: 0, vendidasKwh: 0, aprovadosKwh: 0, reprovadosKwh: 0, efetivasKwh: 0, perdidasKwh: 0, statusCounts: {}, totalDaysToWin: 0, totalDaysToLose: 0 };
            });
        }

        const dayMap = new Map<string, any>();
        const cursor = new Date(start);
        while (cursor <= end) {
            if (!onlyBusinessDays || (cursor.getDay() !== 0 && cursor.getDay() !== 6)) {
                const key = cursor.toISOString().split('T')[0];
                const label = cursor.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                const fullLabel = cursor.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

                const entry: any = { date: key, label, fullLabel, criadas: 0, vendidas: 0, perdidas: 0 };
                if (isComp) {
                    dashboardResponsibles.forEach(id => {
                        entry[`criadas_${id}`] = 0;
                        entry[`vendidas_${id}`] = 0;
                        entry[`perdidas_${id}`] = 0;
                    });
                }
                dayMap.set(key, entry);
            }
            cursor.setDate(cursor.getDate() + 1);
        }

        criadasInRange.forEach(r => {
            const key = new Date(r.created_at).toISOString().split('T')[0];
            const entry = dayMap.get(key);
            if (entry) {
                entry.criadas++;
                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    entry[`criadas_${r.responsavel_id}`]++;
                }
            }
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].criadas++;
                respStats[r.responsavel_id].criadasKwh += (r.consumo_medio_kwh || 0);
            }
        });
        // Gráfico: vendidas por data_assinatura_termo (quando assinou)
        vendidasInRange.forEach(r => {
            const key = new Date(r.data_assinatura_termo!).toISOString().split('T')[0];
            const entry = dayMap.get(key);
            if (entry) {
                entry.vendidas++;
                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    entry[`vendidas_${r.responsavel_id}`]++;
                }
            }
        });
        // respStats: usa kpiAssinados (cohorte do período) para consistência com o card
        kpiAssinados.forEach(r => {
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].vendidas++;
                const diff = new Date(r.data_assinatura_termo!).getTime() - new Date(r.created_at).getTime();
                respStats[r.responsavel_id].totalDaysToWin += diff / (1000 * 60 * 60 * 24);
            }
        });

        perdidasInRange.forEach(r => {
            const lossTs = perdaDateMap[r.id] || r.updated_at;
            const key = new Date(lossTs).toISOString().split('T')[0];
            const entry = dayMap.get(key);
            if (entry) {
                entry.perdidas++;
                if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                    entry[`perdidas_${r.responsavel_id}`]++;
                }
            }
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].perdidas++;
                respStats[r.responsavel_id].perdidasKwh += (r.consumo_medio_kwh || 0);
                const lossTs = perdaDateMap[r.id] || r.updated_at;
                const diff = new Date(lossTs).getTime() - new Date(r.created_at).getTime();
                if (diff > 0) respStats[r.responsavel_id].totalDaysToLose += diff / (1000 * 60 * 60 * 24);
            }
        });

        aprovadosInRange.forEach(r => {
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].aprovados++;
                respStats[r.responsavel_id].aprovadosKwh += (r.consumo_medio_kwh || 0);
            }
        });
        reprovadosInRange.forEach(r => {
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].reprovados++;
                respStats[r.responsavel_id].reprovadosKwh += (r.consumo_medio_kwh || 0);
            }
        });
        efetivasInRange.forEach(r => {
            if (isComp && r.responsavel_id && respStats[r.responsavel_id]) {
                respStats[r.responsavel_id].efetivas++;
                respStats[r.responsavel_id].efetivasKwh += (r.consumo_medio_kwh || 0);
            }
        });

        const chartData = Array.from(dayMap.values());

        const totalCriadas = chartData.reduce((a, d) => a + d.criadas, 0);
        const totalVendidas = chartData.reduce((a, d) => a + d.vendidas, 0);
        const totalPerdidas = chartData.reduce((a, d) => a + d.perdidas, 0);
        const days = chartData.length || 1;
        const avgCriadas = (totalCriadas / days).toFixed(1);
        const avgVendidas = (totalVendidas / days).toFixed(1);
        const avgPerdidas = (totalPerdidas / days).toFixed(1);

        const statusCounts = {
            em_andamento: 0, ganho: 0, perdido: 0, em_pausa: 0, simulacao_realizada: 0,
            termo_enviado: 0, termo_assinado: 0, termo_analise: 0, termo_aprovado: 0, termo_reprovado: 0
        };

        rows.forEach(r => {
            const d = new Date(r.updated_at);
            if (d >= start && d <= end) {
                if (onlyBusinessDays && (d.getDay() === 0 || d.getDay() === 6)) return;
                if (r.status in statusCounts) {
                    statusCounts[r.status as keyof typeof statusCounts]++;
                }
            }
        });

        // Compute accurate statusCounts per responsible
        if (isComp) {
            rows.forEach(r => {
                if (r.responsavel_id && respStats[r.responsavel_id]) {
                    // It's probably better to use the exact same logic we just wrote for aprovados/reprovados
                    // But wait, the previous code just did:
                    // respStats[r.responsavel_id].statusCounts[r.status]++
                    const d = new Date(r.updated_at);
                    if (d >= start && d <= end) {
                        if (!onlyBusinessDays || (d.getDay() !== 0 && d.getDay() !== 6)) {
                            respStats[r.responsavel_id].statusCounts[r.status] = (respStats[r.responsavel_id].statusCounts[r.status] || 0) + 1;
                        }
                    }
                }
            });

            // Overwrite statusCounts for aprovado/reprovado based on analise_cadastral to match new logic
            aprovadosInRange.forEach(r => {
                if (r.responsavel_id && respStats[r.responsavel_id]) {
                    respStats[r.responsavel_id].statusCounts['termo_aprovado'] = (respStats[r.responsavel_id].statusCounts['termo_aprovado_ac'] || 0) + 1;
                    respStats[r.responsavel_id].statusCounts['termo_aprovado_ac'] = respStats[r.responsavel_id].statusCounts['termo_aprovado'];
                }
            });
            reprovadosInRange.forEach(r => {
                if (r.responsavel_id && respStats[r.responsavel_id]) {
                    respStats[r.responsavel_id].statusCounts['termo_reprovado'] = (respStats[r.responsavel_id].statusCounts['termo_reprovado_ac'] || 0) + 1;
                    respStats[r.responsavel_id].statusCounts['termo_reprovado_ac'] = respStats[r.responsavel_id].statusCounts['termo_reprovado'];
                }
            });
        }

        return {
            statusCounts, respStats,
            criadas: criadasInRange.length,
            criadasKwh,
            vendidas: kpiAssinados.length,
            assinadosKwh,
            aprovados: aprovadosInRange.length,
            aprovadosKwh,
            reprovados: reprovadosInRange.length,
            reprovadosKwh,
            efetivas: efetivasInRange.length,
            efetivasKwh,
            perdidas: perdidasInRange.length,
            perdidasKwh,
            avgDaysToWin,
            avgDaysToLose,
            chartData,
            totalCriadas, totalVendidas, totalPerdidas,
            avgCriadas, avgVendidas, avgPerdidas, days
        };
    }, [rows, perdaDateMap, decisaoMap, dateStart, dateEnd, responsibleMode, dashboardResponsibles, profiles, onlyBusinessDays]);

    // KPIs do período ANTERIOR (mesma duração, imediatamente antes) — base dos deltas ↑↓.
    const prevKpis = useMemo(() => {
        const curStart = new Date(dateStart + 'T00:00:00');
        const curEnd = new Date(dateEnd + 'T23:59:59');
        const lenMs = curEnd.getTime() - curStart.getTime();
        const prevEnd = new Date(curStart.getTime() - 1);
        const prevStart = new Date(prevEnd.getTime() - lenMs);
        const isBiz = (d: Date) => !onlyBusinessDays || (d.getDay() !== 0 && d.getDay() !== 6);
        const noPeriodoAnterior = (iso: string | null) => {
            if (!iso) return false;
            const d = new Date(iso);
            return d >= prevStart && d <= prevEnd && isBiz(d);
        };

        // Mesmo critério de computed() acima: Criadas e Assinados pela data do SEU
        // evento; Aprovados/Reprovados por decisao_final_at (decisaoMap); Perdidas
        // continua por criação (ver o comentário longo em computed() sobre por que
        // updated_at e a data do log de perda não são seguras pra essa ainda).
        const criadas = rows.filter(r => noPeriodoAnterior(r.created_at));
        const assinados = rows.filter(r => noPeriodoAnterior(r.data_assinatura_termo));
        // Mesmo gate de status real (r.status) usado em computed() — rascunho de
        // decisao_final sem "Finalizar Análise" não conta como aprovação/reprovação.
        const aprovados = rows.filter(r => {
            const dec = decisaoMap[r.id];
            return !!dec && (dec.decisao === 'Aprovado' || dec.decisao === 'Aprovado com Ressalvas')
                && noPeriodoAnterior(dec.data) && r.status === 'termo_aprovado';
        });
        const reprovados = rows.filter(r => {
            const dec = decisaoMap[r.id];
            return !!dec && dec.decisao === 'Reprovado' && noPeriodoAnterior(dec.data) && r.status === 'termo_reprovado';
        });
        const efetivas = rows.filter(r => ehVendaEfetiva(r, noPeriodoAnterior));
        const perdidas = criadas.filter(r => r.status === 'perdido');

        let avgDaysToWin = 0;
        if (assinados.length > 0) {
            const total = assinados.reduce((acc, r) => acc + (new Date(r.data_assinatura_termo!).getTime() - new Date(r.created_at).getTime()) / 86_400_000, 0);
            avgDaysToWin = Math.round(total / assinados.length);
        }
        let avgDaysToLose = 0;
        if (perdidas.length > 0) {
            let total = 0, count = 0;
            perdidas.forEach(r => {
                const lossDate = perdaDateMap[r.id] || r.updated_at;
                const diff = (new Date(lossDate).getTime() - new Date(r.created_at).getTime()) / 86_400_000;
                if (diff > 0) { total += diff; count++; }
            });
            avgDaysToLose = count > 0 ? Math.round(total / count) : 0;
        }
        return {
            criadas: criadas.length, vendidas: assinados.length, aprovados: aprovados.length,
            reprovados: reprovados.length, efetivas: efetivas.length, perdidas: perdidas.length, avgDaysToWin, avgDaysToLose,
        };
    }, [rows, perdaDateMap, decisaoMap, dateStart, dateEnd, onlyBusinessDays]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowResponsibleDropdown(false);
            }
        };
        if (showResponsibleDropdown) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showResponsibleDropdown]);

    if (!isOpen) return null;

    const updatedAt = new Date().toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });

    // Responsible label for dropdown button
    const getResponsibleLabel = () => {
        if (responsibleMode === 'mine') return 'Minhas negociações';
        if (responsibleMode === 'all') return 'Todas as negociações';
        if (dashboardResponsibles.length === 0) return 'Todas as negociações';
        const names = dashboardResponsibles
            .map(id => profiles.find(p => p.id === id)?.full_name)
            .filter(Boolean);
        if (names.length <= 2) return names.join(', ');
        return `${names[0]}, +${names.length - 1}`;
    };

    const filteredProfiles = profiles.filter(p => {
        const matchesSearch = p.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
        const isManagedOrSelf =
            profile?.role === 'parceiro' ? (p.parceiro_id === profile.id || p.id === profile.id) :
                profile?.role === 'gerente_parcerias' ? (p.role === 'parceiro' || p.role === 'vendedor_parceiro' || p.id === profile.id) :
                    true;
        return matchesSearch && isManagedOrSelf;
    });

    const openResponsibleDropdown = () => {
        if (isVendedor) return;
        setPendingResponsibles([...dashboardResponsibles]);
        setPendingMode(responsibleMode);
        setShowResponsibleDropdown(!showResponsibleDropdown);
    };

    const handleSelectResponsible = (id: string) => {
        const next = pendingResponsibles.includes(id)
            ? pendingResponsibles.filter(x => x !== id)
            : [...pendingResponsibles, id];
        setPendingResponsibles(next);
        setPendingMode(next.length === 0 ? 'all' : 'custom');
    };

    const applyResponsibles = () => {
        setDashboardResponsibles([...pendingResponsibles]);
        setResponsibleMode(pendingMode);
        setShowResponsibleDropdown(false);
    };

    const exportDashboard = () => {
        const kpis: (string | number)[][] = [
            ['Negociações criadas', computed.criadas, prevKpis.criadas],
            ['Termos assinados', computed.vendidas, prevKpis.vendidas],
            ['Vendas efetivas (assinadas no período e aprovadas)', computed.efetivas, prevKpis.efetivas],
            ['Termos aprovados', computed.aprovados, prevKpis.aprovados],
            ['Termos reprovados', computed.reprovados, prevKpis.reprovados],
            ['Negociações perdidas', computed.perdidas, prevKpis.perdidas],
            ['Tempo médio até assinatura (dias)', computed.avgDaysToWin, prevKpis.avgDaysToWin],
            ['Tempo médio até perda (dias)', computed.avgDaysToLose, prevKpis.avgDaysToLose],
        ];
        const wsKpis = buildSheet(['Indicador', 'Período', 'Período anterior'], kpis, ['txt', 'num', 'num'], { widths: [34, 14, 18] });
        const wsDaily = buildSheet(
            ['Data', 'Criadas', 'Convertidas', 'Perdidas'],
            computed.chartData.map((d: any) => [d.fullLabel, d.criadas, d.vendidas, d.perdidas]),
            ['txt', 'num', 'num', 'num'],
            { totals: ['Total', computed.totalCriadas, computed.totalVendidas, computed.totalPerdidas] },
        );
        downloadWorkbook([
            { name: 'KPIs', ws: wsKpis },
            { name: 'Série diária', ws: wsDaily },
        ], `Dashboard_Negociacoes_${dateStart}_a_${dateEnd}_${fileStamp()}.xlsx`);
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-2 sm:p-4 md:p-10">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
            />
            {/* Modal Container */}
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl w-full max-w-[95vw] shadow-2xl flex flex-col my-auto mt-0 md:mt-0 animate-in zoom-in-95 slide-in-from-top-4 duration-300">

                {/* Header */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between p-4 md:p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 rounded-t-2xl relative z-10 gap-4">
                    <div className="flex items-center justify-between w-full lg:w-auto">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-primary/10 text-primary rounded-xl flex-shrink-0">
                                <BarChart3 size={22} />
                            </div>
                            <div>
                                <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:text-white font-poppins">Dashboard de Negociações</h2>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Visão geral do funil no período</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors lg:hidden shrink-0"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto">
                        {/* Date Filter */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm w-full lg:w-auto">
                            <div className="flex items-center gap-2 justify-between sm:justify-start w-full sm:w-auto">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase hidden sm:inline-block">Período:</span>
                                <div className="flex items-center justify-between sm:justify-start gap-2 flex-1 sm:flex-none">
                                    <input
                                        type="date"
                                        value={dateStart}
                                        onChange={e => setDateStart(e.target.value)}
                                        className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary w-full sm:w-auto"
                                    />
                                    <span className="text-gray-400">-</span>
                                    <input
                                        type="date"
                                        value={dateEnd}
                                        onChange={e => setDateEnd(e.target.value)}
                                        className="text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1 outline-none focus:border-primary w-full sm:w-auto"
                                    />
                                </div>
                            </div>
                            <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>
                            <button
                                onClick={() => {
                                    const now = new Date();
                                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                                    setDateStart(firstDay.toISOString().split('T')[0]);
                                    setDateEnd(lastDay.toISOString().split('T')[0]);
                                }}
                                className="flex items-center justify-center gap-2 px-3 py-1.5 sm:py-1 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors w-full sm:w-auto"
                            >
                                <Calendar size={14} />
                                Mês Atual
                            </button>
                        </div>
                        <button
                            onClick={exportDashboard}
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold border rounded-lg transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-emerald-900/20 shrink-0"
                            title="Exportar KPIs e série diária para Excel"
                        >
                            <Download size={16} />
                            <span className="hidden sm:inline">Exportar</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors hidden lg:block shrink-0"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 p-4 md:p-6 space-y-6 relative z-20">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64">
                            <LoadingSpinner label="Carregando dados..." size="sm" />
                        </div>
                    ) : (
                        <>
                            {/* KPI Cards */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3 md:gap-4">
                                {/* Negociações Criadas */}
                                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">Negociações <br /> Criadas</p>
                                    <p className="text-3xl font-extrabold text-blue-800 dark:text-blue-300 font-mono leading-none mt-1">{computed.criadas}</p>
                                    <p className="text-xs text-blue-500 dark:text-blue-400 font-mono mt-1">
                                        {computed.criadasKwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                                    </p>
                                    <div className="mt-1"><DeltaBadge atual={computed.criadas} anterior={prevKpis.criadas} /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[10px]">
                                                    <span className="flex items-center gap-1 text-blue-700/80 dark:text-blue-300/80 truncate">
                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-blue-800 dark:text-blue-300">{computed.respStats[id]?.criadas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Termos Assinados */}
                                <div className="bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900/50 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">Termos <br /> Assinados</p>
                                    <p className="text-3xl font-extrabold text-green-800 dark:text-green-300 font-mono leading-none mt-1">{computed.vendidas}</p>
                                    <p className="text-xs text-green-500 dark:text-green-400 font-mono mt-1">
                                        {computed.assinadosKwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                                    </p>
                                    <div className="mt-1"><DeltaBadge atual={computed.vendidas} anterior={prevKpis.vendidas} /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[10px]">
                                                    <span className="flex items-center gap-1 text-green-700/80 dark:text-green-300/80 truncate">
                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-teal-800 dark:text-teal-300">{computed.respStats[id]?.vendidas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Vendas Efetivas (#6421) — assinadas no período e já aprovadas */}
                                <div className="bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-200 dark:border-emerald-800/60 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <div className="flex items-start justify-between mb-1 gap-1">
                                        <p className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide leading-tight">Vendas <br /> Efetivas</p>
                                        <div className="group relative flex-shrink-0">
                                            <Info size={13} className="text-emerald-500 cursor-help" />
                                            <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-[11px] px-2 py-1 rounded-lg w-56 z-10 shadow-lg">
                                                Termos assinados no período que já foram aprovados na validação de dados. Quem ainda está em análise entra quando for aprovado. "Termos Aprovados" é outra conta: vai pela data da aprovação e inclui termos assinados antes do período.
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-3xl font-extrabold text-emerald-800 dark:text-emerald-300 font-mono leading-none mt-1">{computed.efetivas}</p>
                                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-mono mt-1">
                                        {computed.efetivasKwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                                    </p>
                                    <p className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80 mt-0.5">das {computed.vendidas} assinadas</p>
                                    <div className="mt-1"><DeltaBadge atual={computed.efetivas} anterior={prevKpis.efetivas} /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[10px]">
                                                    <span className="flex items-center gap-1 text-emerald-700/80 dark:text-emerald-300/80 truncate">
                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-emerald-800 dark:text-emerald-300">{computed.respStats[id]?.efetivas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Termos Aprovados */}
                                <div className="bg-lime-50 dark:bg-lime-950/30 border border-lime-100 dark:border-lime-900/50 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-[10px] font-bold text-lime-600 dark:text-lime-400 uppercase tracking-wide mb-1">Termos <br /> Aprovados</p>
                                    <p className="text-3xl font-extrabold text-lime-800 dark:text-lime-300 font-mono leading-none mt-1">{computed.aprovados}</p>
                                    <p className="text-xs text-lime-500 dark:text-lime-400 font-mono mt-1">
                                        {computed.aprovadosKwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                                    </p>
                                    <div className="mt-1"><DeltaBadge atual={computed.aprovados} anterior={prevKpis.aprovados} /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[10px]">
                                                    <span className="flex items-center gap-1 text-lime-700/80 dark:text-lime-300/80 truncate">
                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-lime-800 dark:text-lime-300">{computed.respStats[id]?.aprovados || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Termos Reprovados */}
                                <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/50 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wide mb-1">Termos <br /> Reprovados</p>
                                    <p className="text-3xl font-extrabold text-rose-800 dark:text-rose-300 font-mono leading-none mt-1">{computed.reprovados}</p>
                                    <p className="text-xs text-rose-500 dark:text-rose-400 font-mono mt-1">
                                        {computed.reprovadosKwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                                    </p>
                                    <div className="mt-1"><DeltaBadge atual={computed.reprovados} anterior={prevKpis.reprovados} inverso /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[10px]">
                                                    <span className="flex items-center gap-1 text-rose-700/80 dark:text-rose-300/80 truncate">
                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-rose-800 dark:text-rose-300">{computed.respStats[id]?.reprovados || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Negociações Perdidas */}
                                <div className="bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">Negociações <br /> Perdidas</p>
                                    <p className="text-3xl font-extrabold text-red-800 dark:text-red-300 font-mono leading-none mt-1">{computed.perdidas}</p>
                                    <p className="text-xs text-red-500 dark:text-red-400 font-mono mt-1">
                                        {computed.perdidasKwh.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh
                                    </p>
                                    <div className="mt-1"><DeltaBadge atual={computed.perdidas} anterior={prevKpis.perdidas} inverso /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => (
                                                <div key={id} className="flex items-center justify-between text-[10px]">
                                                    <span className="flex items-center gap-1 text-red-700/80 dark:text-red-300/80 truncate">
                                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                        <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-red-800 dark:text-red-300">{computed.respStats[id]?.perdidas || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Tempo Médio até Assinatura */}
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <div className="flex items-start justify-between mb-1 gap-1">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-tight">Tempo Médio <br /> até Assinatura</p>
                                        <div className="group relative flex-shrink-0">
                                            <Info size={13} className="text-gray-400 cursor-help" />
                                            <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-[11px] px-2 py-1 rounded-lg w-48 z-10 shadow-lg">
                                                Média de dias entre a criação e a data de assinatura do termo, para termos assinados no período
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-1 mt-1">
                                        <span className="text-3xl font-extrabold text-gray-800 dark:text-white font-mono leading-none">{computed.avgDaysToWin}</span>
                                        <span className="text-xs text-gray-500 font-medium">dias</span>
                                    </div>
                                    <div className="mt-1"><DeltaBadge atual={computed.avgDaysToWin} anterior={prevKpis.avgDaysToWin} inverso suffix="d" /></div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => {
                                                const stats = computed.respStats[id];
                                                const avg = stats?.vendidas ? Math.round(stats.totalDaysToWin / stats.vendidas) : 0;
                                                return (
                                                    <div key={id} className="flex items-center justify-between text-[10px]">
                                                        <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400 truncate">
                                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                            <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                        </span>
                                                        <span className="font-bold text-gray-800 dark:text-gray-200">{avg}d</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Negociações Excluídas */}
                                <div className="bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Negociações <br /> Excluídas</p>
                                    <p className="text-3xl font-extrabold text-gray-700 dark:text-gray-300 font-mono leading-none mt-1">{excluidas}</p>
                                    <p className="text-[10px] text-gray-400 font-mono mt-1">registros</p>
                                </div>

                                {/* Tempo Médio até Perda — não alterado */}
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 transition-all hover:shadow-md flex flex-col">
                                    <div className="flex items-start justify-between mb-1 gap-1">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-tight">Tempo Médio <br /> até Perda</p>
                                        <div className="group relative flex-shrink-0">
                                            <Info size={13} className="text-gray-400 cursor-help" />
                                            <div className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-[11px] px-2 py-1 rounded-lg w-48 z-10 shadow-lg">
                                                Média de dias entre a criação e o fechamento como "perdido"
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-1 mt-1">
                                        <span className="text-3xl font-extrabold text-gray-800 dark:text-white font-mono leading-none">{computed.avgDaysToLose}</span>
                                        <span className="text-xs text-gray-500 font-medium">dias</span>
                                    </div>
                                    {isComparative && (
                                        <div className="mt-auto pt-2 space-y-1">
                                            {dashboardResponsibles.map((id, idx) => {
                                                const stats = computed.respStats[id];
                                                const avg = stats?.perdidas ? Math.round(stats.totalDaysToLose / stats.perdidas) : 0;
                                                return (
                                                    <div key={id} className="flex items-center justify-between text-[10px]">
                                                        <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400 truncate">
                                                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[idx % colors.length] }}></span>
                                                            <span className="truncate">{profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || '—'}</span>
                                                        </span>
                                                        <span className="font-bold text-gray-800 dark:text-gray-200">{avg}d</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 md:p-6">
                                {/* Section Header */}
                                <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-6 gap-4 relative z-50">
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 dark:text-white">
                                            {isComparative ? 'Comparativo de Equipe' : 'Negociações criadas, convertidas e perdidas'}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                            <Info size={12} />
                                            Atualizado em {updatedAt}
                                        </p>
                                    </div>

                                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full xl:w-auto">
                                        <div className="flex overflow-x-auto hide-scrollbar custom-scrollbar pb-1 sm:pb-0 gap-3 w-full sm:w-auto flex-1 sm:flex-none">
                                            <button
                                                onClick={() => setOnlyBusinessDays(!onlyBusinessDays)}
                                                className={`flex-shrink-0 px-3 py-2 sm:py-1.5 rounded-lg text-xs font-semibold transition-all border ${onlyBusinessDays ? 'bg-primary/10 border-primary/30 text-primary shadow-sm' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                                title="Ignorar sábados e domingos no gráfico e médias"
                                            >
                                                {onlyBusinessDays ? 'Apenas Dias Úteis' : 'Todos os Dias'}
                                            </button>

                                            {isComparative && chartType === 'line' && (
                                                <div className="flex-shrink-0 flex items-center gap-1 sm:gap-2 bg-gray-50 dark:bg-gray-800/50 p-1 sm:p-1.5 rounded-xl border border-gray-200 dark:border-gray-700">
                                                    <button
                                                        onClick={() => setCompareMetric('criadas')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${compareMetric === 'criadas' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 shadow-sm' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Criadas
                                                    </button>
                                                    <button
                                                        onClick={() => setCompareMetric('vendidas')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${compareMetric === 'vendidas' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 shadow-sm' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Convertidas
                                                    </button>
                                                    <button
                                                        onClick={() => setCompareMetric('perdidas')}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${compareMetric === 'perdidas' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 shadow-sm' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                                    >
                                                        Perdidas
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
                                            {/* Responsible Selector Dropdown */}
                                            <div className="relative z-50 flex-1 sm:flex-none" ref={dropdownRef}>
                                                <ResponsibleFilter
                                                    selectedResponsibles={
                                                        responsibleMode === 'mine' ? ['me'] : dashboardResponsibles
                                                    }
                                                    setSelectedResponsibles={(keys) => {
                                                        if (keys.includes('me')) {
                                                            setResponsibleMode('mine');
                                                            setDashboardResponsibles(user?.id ? [user.id] : []);
                                                        } else {
                                                            setResponsibleMode(keys.length === 0 ? 'all' : 'custom');
                                                            setDashboardResponsibles(keys);
                                                        }
                                                    }}
                                                    profiles={profiles as any} // ProfileItem might need casting
                                                    currentUserProfile={profile}
                                                    viewContext="crm"
                                                />
                                            </div>

                                            {/* View Toggle: Line / Table */}
                                            <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                                <button
                                                    onClick={() => setChartType('line')}
                                                    className={`p-1.5 rounded-md transition-all ${chartType === 'line'
                                                        ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                                        : 'text-gray-400 hover:text-gray-600'
                                                        }`}
                                                    title="Gráfico de linha"
                                                >
                                                    <LineChartIcon size={16} />
                                                </button>
                                                <button
                                                    onClick={() => setChartType('table')}
                                                    className={`p-1.5 rounded-md transition-all ${chartType === 'table'
                                                        ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                                        : 'text-gray-400 hover:text-gray-600'
                                                        }`}
                                                    title="Visualizar como tabela"
                                                >
                                                    <Table2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Content: Chart or Table */}
                                {chartType === 'line' ? (
                                    <div style={{ width: '100%', height: 320 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={computed.chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                <XAxis
                                                    dataKey="label"
                                                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                                                    axisLine={{ stroke: '#e5e7eb' }}
                                                    tickLine={false}
                                                    interval="preserveStartEnd"
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    allowDecimals={false}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: '#fff',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '12px',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                        fontSize: 13,
                                                    }}
                                                    labelStyle={{ fontWeight: 700, color: '#374151' }}
                                                />
                                                {!isComparative ? (
                                                    <>
                                                        <Line type="monotone" dataKey="criadas" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Criadas" />
                                                        <Line type="monotone" dataKey="vendidas" stroke="#22c55e" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Convertidas" />
                                                        <Line type="monotone" dataKey="perdidas" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Perdidas" />
                                                    </>
                                                ) : (
                                                    dashboardResponsibles.map((id, idx) => {
                                                        const name = profiles.find(p => p.id === id)?.full_name?.split(' ')[0] || 'Desconhecido';
                                                        return (
                                                            <Line
                                                                key={id}
                                                                type="monotone"
                                                                dataKey={`${compareMetric}_${id}`}
                                                                stroke={colors[idx % colors.length]}
                                                                strokeWidth={2.5}
                                                                dot={{ r: 3 }}
                                                                activeDot={{ r: 5 }}
                                                                name={name}
                                                            />
                                                        );
                                                    })
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    /* Table View */
                                    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-xl">
                                        <div className="max-h-[400px] overflow-y-auto custom-scrollbar min-w-[600px]">
                                            {!isComparative ? (
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Negociações Criadas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Negociações Convertidas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Negociações Perdidas</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                                                        {computed.chartData.map((day, idx) => (
                                                            <tr key={day.date} className={`transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-200">{day.fullLabel}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{day.criadas}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{day.vendidas}</td>
                                                                <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{day.perdidas}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="">
                                                        {/* Total Row */}
                                                        <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-800 dark:text-white uppercase">Total</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-blue-700 dark:text-blue-400 font-mono">{computed.totalCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-green-700 dark:text-green-400 font-mono">{computed.totalVendidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-red-700 dark:text-red-400 font-mono">{computed.totalPerdidas}</td>
                                                        </tr>
                                                        {/* Average Row */}
                                                        <tr className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-600 dark:text-gray-300 uppercase">Média / dia</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgVendidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgPerdidas}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            ) : (
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Responsável</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Criadas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Convertidas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Perdidas</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Taxa de Conversão</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média de Criadas por Dia">Média Criadas/Dia</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média de Convertidas por Dia">Média Convert/Dia</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média de Perdidas por Dia">Média Perdidas/Dia</th>
                                                            <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Média da Taxa de Conversão por Dia">Média Tx. Conv/Dia</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                                                        {dashboardResponsibles
                                                            .map(id => ({ id, name: profiles.find(p => p.id === id)?.full_name || 'Desconhecido', stats: computed.respStats[id] }))
                                                            .sort((a, b) => (b.stats?.vendidas || 0) - (a.stats?.vendidas || 0))
                                                            .map(({ id, name, stats }, idx) => {
                                                                const sInfo = stats || { criadas: 0, vendidas: 0, perdidas: 0 };
                                                                const finalizadas = sInfo.criadas + sInfo.perdidas;
                                                                const conversaoVal = finalizadas > 0 ? (sInfo.vendidas / finalizadas) * 100 : 0;
                                                                const tx = finalizadas > 0 ? conversaoVal.toFixed(1) + '%' : '-';
                                                                const originalIndex = dashboardResponsibles.indexOf(id);

                                                                const avgCriadas = (sInfo.criadas / Math.max(1, computed.days)).toFixed(1);
                                                                const avgVendidas = (sInfo.vendidas / Math.max(1, computed.days)).toFixed(1);
                                                                const avgPerdidas = (sInfo.perdidas / Math.max(1, computed.days)).toFixed(1);
                                                                const avgTx = finalizadas > 0 ? (conversaoVal / Math.max(1, computed.days)).toFixed(1) + '%' : '-';

                                                                return (
                                                                    <tr key={id} className={`transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors[originalIndex % colors.length] }}></span>
                                                                            {name}
                                                                        </td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-blue-600 dark:text-blue-300 font-mono">{sInfo.criadas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-green-600 dark:text-green-300 font-mono font-bold">{sInfo.vendidas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-red-600 dark:text-red-300 font-mono">{sInfo.perdidas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{tx}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgCriadas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgVendidas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgPerdidas}</td>
                                                                        <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400 font-mono">{avgTx}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                    <tfoot className="">
                                                        <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-800 dark:text-white uppercase">Total Equipe</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-blue-700 dark:text-blue-400 font-mono">{computed.totalCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-green-700 dark:text-green-400 font-mono">{computed.totalVendidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-red-700 dark:text-red-400 font-mono">{computed.totalPerdidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-700 dark:text-gray-300 font-mono">
                                                                {(computed.totalCriadas + computed.totalPerdidas) > 0 ? ((computed.totalVendidas / (computed.totalCriadas + computed.totalPerdidas)) * 100).toFixed(1) + '%' : '-'}
                                                            </td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">{computed.avgCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">{computed.avgVendidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">{computed.avgPerdidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center font-bold text-gray-600 dark:text-gray-400 font-mono">
                                                                {(computed.totalCriadas + computed.totalPerdidas) > 0 ? (((computed.totalVendidas / (computed.totalCriadas + computed.totalPerdidas)) * 100) / Math.max(1, computed.days)).toFixed(1) + '%' : '-'}
                                                            </td>
                                                        </tr>
                                                        {/* Average Row Comparativo */}
                                                        <tr className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600">
                                                            <td className="px-6 py-3 text-sm font-bold text-gray-600 dark:text-gray-300 uppercase">Média / dia</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgCriadas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgVendidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">{computed.avgPerdidas}</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                            <td className="px-6 py-3 text-sm text-center text-gray-600 dark:text-gray-300 font-mono">-</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Funil Dinâmico (substitui os cards de status — doc 02 do Bruno) */}
                            <FunilDinamico
                                rows={rows}
                                dateStart={dateStart}
                                dateEnd={dateEnd}
                                onOpenNegociacao={onOpenNegociacao}
                            />
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 flex justify-end rounded-b-2xl relative z-10">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default KanbanGraphic;
